import { CommonModule, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { NumberSuffixPipe, TimezoneAwareDatePipe } from '@twaice-fe/frontend/shared/services';
import { formatCalendarDate } from '@twaice-fe/frontend/shared/utilities';
import { RuleType } from '@twaice-fe/shared/models';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { combineLatest, Observable, filter, map, shareReplay } from 'rxjs';
import { SvgIconModule } from '../../svg-icon/svg-icon.module';
import {
  CLEARED_TAG_FILL_COLOR,
  CLEARED_TAG_TEXT_COLOR,
  ONGOING_CIRCLE_FILL_COLOR,
  ONGOING_TAG_FILL_COLOR,
  ONGOING_TAG_TEXT_COLOR,
  alertIconConfig,
} from '../component-sidebar.config';
import { ComponentSidebarService } from '../component-sidebar.service';
import { BreachedRuleSummary, ComparisonOperator } from '../component-sidebar.types';
import { ComponentRuleBreachDetailsComponent } from './component-rule-breach-details/component-rule-breach-details.component';

@Component({
  selector: 'twaice-fe-component-rule-breach',
  templateUrl: './component-rule-breach.component.html',
  imports: [
    CommonModule,
    ComponentRuleBreachDetailsComponent,
    NgIf,
    NzDividerComponent,
    NzTagComponent,
    NzTooltipDirective,
    SvgIconModule,
  ],
})
export class ComponentRuleBreach implements OnInit {
  @Input() rule: BreachedRuleSummary;
  @Input() selectedComponent: string | null;
  @Output() selected = new EventEmitter<string>();

  showDetails: WritableSignal<boolean> = signal(false);

  readonly selectedDate$: Observable<Date>;
  readonly isHistoricBreach$: Observable<boolean>;

  protected readonly ongoingTagTextColor = ONGOING_TAG_TEXT_COLOR;
  protected readonly clearedTagTextColor = CLEARED_TAG_TEXT_COLOR;
  protected readonly ongoingTagFillColor = ONGOING_TAG_FILL_COLOR;
  protected readonly clearedTagFillColor = CLEARED_TAG_FILL_COLOR;
  protected readonly ongoingCircleFillColor = ONGOING_CIRCLE_FILL_COLOR;
  protected readonly alertIconConfig = alertIconConfig;
  protected readonly ruleType = RuleType;

  constructor(
    private datePipe: TimezoneAwareDatePipe,
    private numberPipe: NumberSuffixPipe,
    private componentSidebarSerivce: ComponentSidebarService
  ) {
    this.selectedDate$ = this.componentSidebarSerivce.getSelectedDate$().pipe(
      filter((d): d is Date => !!d),
      shareReplay(1)
    );
    this.isHistoricBreach$ = combineLatest([this.selectedDate$, this.componentSidebarSerivce.getStorageTimezone$()]).pipe(
      map(([date, tz]) => formatCalendarDate(date, 'UTC') !== formatCalendarDate(new Date(), tz))
    );
  }

  ngOnInit() {
    if (this.rule.ruleBk === this.selectedComponent) {
      this.selected.emit(this.rule?.ruleBk);
      this.showDetails.update(() => true);
    }
  }

  toggleCollapse() {
    if (!this.showDetails()) {
      this.selected.emit(this.rule?.ruleBk);
    }

    this.showDetails.update((val) => !val);
  }

  getLastBreachTimestampLabel(rule: BreachedRuleSummary): string {
    return this.datePipe.transform(new Date(rule.lastBreach), 'MMM d, HH:mm z') ?? '';
  }

  getBreachValueLabel(rule: BreachedRuleSummary) {
    switch (rule.type) {
      case RuleType.ThresholdRule:
        return this.getThresholdRuleDirectionPrefix(rule);
      case RuleType.BMSRule:
      case RuleType.StateRule:
        return null;
    }
  }

  private getThresholdRuleDirectionPrefix({ comparisonOperator, highestBreachedThreshold, unit }: BreachedRuleSummary) {
    let directionPrefix = '';
    switch (comparisonOperator) {
      case ComparisonOperator.GreaterThan:
      case ComparisonOperator.GreaterThanEqual:
        directionPrefix = 'Above';
        break;
      case ComparisonOperator.LessThan:
      case ComparisonOperator.LessThanEqual:
        directionPrefix = 'Below';
    }

    return `${directionPrefix} ${this.numberPipe.transform(highestBreachedThreshold ?? 0, 2, unit)}`;
  }
}
